import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ImgCrop from 'antd-img-crop';
import { Button, Col, Form, Input, Modal, Row, Typography, Upload } from "antd";

import AvatarDefault from '@assets/images/ava.svg'
import RightIcon from "@components/svg/RightIcon";
// import { destroyUser } from "@utils/localStorage";
import { getUserQuery, queryKey } from "@dataLayer/user";
import { API_PATHS, PATHS } from "@config/constants";
import { MODAL_TYPE, ModalSuccess, ModalFail } from "@components/Modal";

import "./index.scss";
import { useLogout, useUpdateInfo } from "dataLayer/user";
import { ExclamationCircleOutlined } from "@ant-design/icons";

const { Title } = Typography;

const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

const AccountProfile = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const { mutate, isLoading, isSuccess, error } = useUpdateInfo();
  const { mutate: mutateLogout, isSuccess: isSuccessLogout } = useLogout();

  const [message, setMessage] = useState(null);
  const [displayModal, setDisplayModal] = useState(false);
  const [modalType, setModalType] = useState(MODAL_TYPE.SUCCESS);

  useEffect(() => {
    if (isSuccess || error) {
      setModalType(isSuccess ? MODAL_TYPE.SUCCESS : MODAL_TYPE.FAIL);
      setMessage(error?.message || t("account.profile_page.update_success"));
      setDisplayModal(true);
    }
  }, [isSuccess, error, t])

  useEffect(() => {
    if (isSuccessLogout) {
      // destroyUser();
      // queryClient.invalidateQueries(queryKey);
      history.push(PATHS.HOME_PAGE);
    }
  }, [isSuccessLogout, history])

  const closeModal = () => setDisplayModal(false);
  const MyModal = modalType === MODAL_TYPE.SUCCESS ? ModalSuccess : ModalFail;

  const { data: user } = useQuery(getUserQuery());
  const [fileList, setFileList] = useState([
    {
      uid: '-1',
      name: 'avatar.svg',
      status: 'done',
      url: user?.image || AvatarDefault,
    },
  ]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [form] = Form.useForm();
  const [fields] = useState([
    {
      name: ['displayName'],
      value: '',
    },
    {
      name: ['emailAddress'],
      value: '',
    },
    {
      name: ['isdn'],
      value: '',
    }
  ]);

  useEffect(() => {
    form.setFieldsValue({
      displayName: user?.name,
      emailAddress: user?.email,
      isdn: user?.msisdn,
    })
  }, [user, form]);

  useEffect(() => {
    setFileList([
      {
        uid: '-1',
        name: 'avatar.svg',
        status: 'done',
        url: user?.image || AvatarDefault,
      },
    ])
  }, [user]);

  const handleCancel = () => {
    setPreviewVisible(false)
  }

  const handlePreview = async file => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    setPreviewVisible(true);
    setPreviewImage(file.url || file.preview);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
  };

  const onChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const onRemove = () => {
    setFileList([{
      uid: '-1',
      name: 'avatar.svg',
      status: 'done',
      url: AvatarDefault,
    }]);
    return false;
  };

  const onFinish = (values) => {
    let image = "";
    if (fileList?.length > 0 && (fileList[0].response?.file || fileList[0].url)) {
      image = fileList[0].response?.file || fileList[0].url;
    }
    mutate({ ...values, image });
  };

  const onLogout = () => {
    Modal.confirm({
      title: t("account.profile_page.form_logout.confirm"),
      icon: <ExclamationCircleOutlined />,
      content: t("account.profile_page.form_logout.message"),
      onOk() {
        mutateLogout();
      },
    });
  }

  return (
    <div className="account-profile">
      <Title level={2} className="account-page-title">{t("account.profile")}</Title>
      <Row gutter={[15, 15]}>
        <Col md={12}>
          <Row gutter={[15, 15]}>
            <Col>
              <div className="account-profile-avatar">
                <ImgCrop rotate modalTitle={t("account.profile_page.edit_image")}>
                  <Upload
                    className="upload-image"
                    listType="picture-card"
                    maxCount={1}
                    accept="image/*"
                    fileList={fileList}
                    onChange={onChange}
                    onPreview={handlePreview}
                    onRemove={onRemove}
                    action={API_PATHS.upload}
                  >
                    <Button className="btn-upload">{t("account.profile_page.upload")}</Button>
                  </Upload>
                </ImgCrop>
                <Modal
                  visible={previewVisible}
                  title={previewTitle}
                  footer={null}
                  onCancel={handleCancel}
                >
                  <img alt="example" style={{ width: '100%' }} src={previewImage} />
                </Modal>
              </div>
            </Col>
          </Row>
        </Col>
        <Col xs={24} md={12}>
          <Form
            className="account-form"
            form={form}
            name="basic"
            layout="vertical"
            initialValues={{
              remember: true,
            }}
            fields={fields}
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item
              label={t("account.profile_page.display_name")}
              name="displayName"
              hasFeedback={true}
              rules={[
                {
                  required: true,
                  message: t("error.missing.name"),
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t("account.profile_page.email")}
              name="emailAddress"
              hasFeedback={true}
              rules={[
                {
                  type: 'email',
                  required: true,
                  message: t("error.missing.email"),
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t("account.profile_page.phone")}
              name="isdn"
            >
              <Input disabled />
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <div className="account-profile-footer">
        <div className="account-profile-footer-wrapper">
          <Row justify="space-between" align="middle">
            <Col xs={12}>
              <Button onClick={onLogout} className="btn-logout">{t("account.profile_page.logout")} <RightIcon /></Button>
            </Col>
            <Col xs={12}>
              <Button loading={isLoading} onClick={form.submit} className="btn-save">{t("account.save")}</Button>
            </Col>
          </Row>
        </div>
      </div>
      <MyModal visible={displayModal} message={message} title={t("account.profile")} handleOk={closeModal} handleCancel={closeModal} />
    </div>
  );
};

export default AccountProfile;
